import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTemplate } from '../../../hooks/useSmartContractTemplates';
import { useRegisterAudit, useMarkAuditFailed, useAuditHistory } from '../../../hooks/useTemplateAudit';
import { AuditStatusBadge } from '../../../components/smart-contracts';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { 
  ArrowLeft, 
  Shield, 
  ShieldCheck, 
  ShieldX, 
  Clock,
  ExternalLink,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

export function TemplateAuditManagement() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: template, isLoading } = useTemplate(id);
  const { data: auditHistory } = useAuditHistory(id);
  const registerAuditMutation = useRegisterAudit();
  const markFailedMutation = useMarkAuditFailed();

  const [mode, setMode] = useState<'register' | 'fail' | null>(null);
  
  // Register audit form
  const [auditorCompany, setAuditorCompany] = useState('');
  const [auditDate, setAuditDate] = useState('');
  const [reportUrl, setReportUrl] = useState('');
  const [findings, setFindings] = useState('');
  
  // Fail audit form
  const [failReason, setFailReason] = useState('');
  const [failAuditor, setFailAuditor] = useState('');

  const [errors, setErrors] = useState<string[]>([]);

  const handleRegisterAudit = async () => {
    const newErrors: string[] = [];
    if (!auditorCompany.trim()) newErrors.push('Empresa auditora é obrigatória');
    if (!auditDate) newErrors.push('Data da auditoria é obrigatória');
    if (!reportUrl.trim()) newErrors.push('URL do relatório é obrigatória');
    
    if (newErrors.length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await registerAuditMutation.mutateAsync({
        templateId: id!,
        dto: {
          auditorCompany: auditorCompany.trim(),
          auditDate,
          reportUrl: reportUrl.trim(),
          findings: findings.trim() || undefined,
        },
      });
      setMode(null);
      setAuditorCompany('');
      setAuditDate('');
      setReportUrl('');
      setFindings('');
      setErrors([]);
    } catch (error) {
      setErrors(['Erro ao registrar auditoria. Tente novamente.']);
    }
  };

  const handleMarkFailed = async () => {
    if (!failReason.trim()) {
      setErrors(['Motivo da falha é obrigatório']);
      return;
    }

    try {
      await markFailedMutation.mutateAsync({
        templateId: id!,
        dto: {
          reason: failReason.trim(),
          auditorCompany: failAuditor.trim() || undefined,
        },
      });
      setMode(null);
      setFailReason('');
      setFailAuditor('');
      setErrors([]);
    } catch (error) {
      setErrors(['Erro ao marcar falha. Tente novamente.']);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="p-6">
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 text-red-400">
          Template não encontrado
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/tokenization/contracts/${id}`)}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Gestão de Auditoria</h1>
          <p className="text-surface-400">{template.name}</p>
        </div>
      </div>

      {/* Current Status */}
      <div className="bg-surface-800 rounded-lg border border-surface-700 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-surface-100 mb-2">Status Atual</h2>
            <AuditStatusBadge
              status={template.auditStatus}
              auditorName={template.auditorCompany}
              auditDate={template.auditDate}
              showDetails
            />
          </div>
          
          <div className="flex gap-2">
            {template.auditStatus !== 'AUDITED' && (
              <Button onClick={() => setMode('register')}>
                <ShieldCheck className="w-4 h-4 mr-2" />
                Registrar Auditoria
              </Button>
            )}
            {template.auditStatus !== 'AUDIT_FAILED' && (
              <Button variant="outline" onClick={() => setMode('fail')}>
                <ShieldX className="w-4 h-4 mr-2" />
                Marcar como Falha
              </Button>
            )}
          </div>
        </div>

        {template.auditReportUrl && (
          <div className="mt-4 pt-4 border-t border-surface-700">
            <a
              href={template.auditReportUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-primary-400 hover:text-primary-300"
            >
              <ExternalLink className="w-4 h-4" />
              Ver Relatório de Auditoria
            </a>
          </div>
        )}
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-400 mb-2">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">Corrija os erros:</span>
          </div>
          <ul className="list-disc list-inside text-red-300 text-sm">
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Register Audit Form */}
      {mode === 'register' && (
        <div className="bg-surface-800 rounded-lg border border-surface-700 p-6 space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="w-5 h-5 text-green-400" />
            <h2 className="text-lg font-medium text-surface-100">Registrar Auditoria</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-surface-400 mb-1">Empresa Auditora *</label>
              <input
                type="text"
                value={auditorCompany}
                onChange={(e) => setAuditorCompany(e.target.value)}
                placeholder="Ex: CertiK, OpenZeppelin, Trail of Bits"
                className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm text-surface-400 mb-1">Data da Auditoria *</label>
              <input
                type="date"
                value={auditDate}
                onChange={(e) => setAuditDate(e.target.value)}
                className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-surface-400 mb-1">URL do Relatório *</label>
            <input
              type="url"
              value={reportUrl}
              onChange={(e) => setReportUrl(e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm text-surface-400 mb-1">Observações / Findings</label>
            <textarea
              value={findings}
              onChange={(e) => setFindings(e.target.value)}
              placeholder="Resumo dos achados da auditoria..."
              rows={3}
              className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="ghost" onClick={() => { setMode(null); setErrors([]); }}>
              Cancelar
            </Button>
            <Button onClick={handleRegisterAudit} disabled={registerAuditMutation.isPending}>
              <CheckCircle className="w-4 h-4 mr-2" />
              {registerAuditMutation.isPending ? 'Salvando...' : 'Confirmar Auditoria'}
            </Button>
          </div>
        </div>
      )}

      {/* Mark Failed Form */}
      {mode === 'fail' && (
        <div className="bg-surface-800 rounded-lg border border-surface-700 p-6 space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <ShieldX className="w-5 h-5 text-red-400" />
            <h2 className="text-lg font-medium text-surface-100">Marcar Auditoria como Falha</h2>
          </div>

          <div>
            <label className="block text-sm text-surface-400 mb-1">Empresa Auditora</label>
            <input
              type="text"
              value={failAuditor}
              onChange={(e) => setFailAuditor(e.target.value)}
              placeholder="Ex: CertiK"
              className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm text-surface-400 mb-1">Motivo da Falha *</label>
            <textarea
              value={failReason}
              onChange={(e) => setFailReason(e.target.value)}
              placeholder="Descreva os problemas encontrados..."
              rows={4}
              className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="ghost" onClick={() => { setMode(null); setErrors([]); }}>
              Cancelar
            </Button>
            <Button variant="outline" onClick={handleMarkFailed} disabled={markFailedMutation.isPending}>
              <ShieldX className="w-4 h-4 mr-2" />
              {markFailedMutation.isPending ? 'Salvando...' : 'Confirmar Falha'}
            </Button>
          </div>
        </div>
      )}

      {/* Audit History */}
      <div className="bg-surface-800 rounded-lg border border-surface-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-surface-700 flex items-center gap-2">
          <Clock className="w-5 h-5 text-primary-400" />
          <h2 className="font-medium text-surface-100">Histórico de Versões</h2>
        </div>
        
        {auditHistory && auditHistory.length > 0 ? (
          <div className="divide-y divide-surface-700">
            {auditHistory.map((record) => (
              <div key={record.id} className="px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-surface-100">v{record.version}</span>
                    <AuditStatusBadge status={record.auditStatus} />
                  </div>
                  <span className="text-sm text-surface-400">
                    {new Date(record.createdAt).toLocaleDateString('pt-BR')}
                  </span>
                </div>
                {record.auditorCompany && (
                  <p className="text-sm text-surface-400 mt-1">
                    Auditor: {record.auditorCompany}
                    {record.auditDate && ` • ${new Date(record.auditDate).toLocaleDateString('pt-BR')}`}
                  </p>
                )}
                <p className="text-xs text-surface-500 mt-1 font-mono truncate">
                  Hash: {record.codeHash}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-surface-400">
            Nenhum histórico disponível
          </div>
        )}
      </div>
    </div>
  );
}

export default TemplateAuditManagement;
